import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import { articleHeroImages, articleRelatedImages } from '@/mocks/articleImages';
import { siteConfig } from '@/mocks/navigation';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import JsonLd from '@/components/feature/JsonLd';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-300">
                <i className="ri-file-unknow-line text-4xl"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
              <Link
                to="/category/knowledge"
                className="inline-block px-5 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                返回文章列表
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://haolianxi.com/' },
      { '@type': 'ListItem', position: 2, name: category?.name || '文章', item: `https://haolianxi.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://haolianxi.com/article/${article.slug}` },
    ],
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://haolianxi.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    breadcrumb: breadcrumbJsonLd,
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - ${siteConfig.name}`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://haolianxi.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogUrl={`https://haolianxi.com/article/${article.slug}`}
      />
      <JsonLd data={articleJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <article className="max-w-3xl mx-auto px-4 md:px-6 py-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                {category?.name || '文章'}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 line-clamp-1">{article.title}</span>
            </nav>

            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-foreground-700 font-medium">{article.author}</span>
                </div>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.readTime}</span>
              </div>
            </header>

            <div className="relative w-full aspect-[21/9] overflow-hidden rounded-lg mb-10 bg-background-200">
              <img
                src={articleHeroImages[article.slug]}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed
                prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600
                prose-strong:text-foreground-900
                prose-li:text-foreground-700 prose-li:leading-relaxed
                prose-ul:my-4 prose-ol:my-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-3xl mx-auto px-4 md:px-6 pb-16" aria-labelledby="related-heading">
              <h2 id="related-heading" className="text-xl font-bold font-heading text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full aspect-[16/10] overflow-hidden bg-background-200">
                      <img
                        src={articleRelatedImages[related.slug]}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}